import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Plane, AlertCircle } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { lawyerBookingsAPI } from '@/utils/api';
import { cn } from '@/lib/utils';

export function BookingSearch({ className, baseRoute = '/lawyer/bookings' }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await lawyerBookingsAPI.search(query);
        setResults(response.results || []);
        setIsOpen(true);
        setSelectedIndex(0);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen || results.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % results.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            handleSelect(results[selectedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          inputRef.current?.blur();
          break;
      }
    },
    [isOpen, results, selectedIndex]
  );

  const handleSelect = (result) => {
    setQuery('');
    setIsOpen(false);
    navigate({ to: `${baseRoute}/${result.id}` });
  };

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !inputRef.current?.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Highlight matching text
  const highlightMatch = (text, query) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={i} className="bg-highlight text-highlight-text font-semibold">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <div className={cn('relative', className)}>
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value.toUpperCase())}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length >= 2 && results.length > 0 && setIsOpen(true)}
          placeholder="Buchungen nach PNR oder Nachname suchen..."
          className={cn(
            'w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200',
            'bg-white shadow-sm',
            'text-slate-900 placeholder:text-slate-400',
            'focus:outline-none focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active',
            'transition-all duration-150'
          )}
        />
        {isLoading && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <div className="h-5 w-5 border-2 border-slate-200 border-t-sidebar-active rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50"
        >
          {results.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <AlertCircle className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Keine Buchungen gefunden für „{query}"</p>
              <p className="text-xs text-slate-400 mt-1">
                Versuchen Sie eine andere PNR oder einen anderen Nachnamen
              </p>
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {results.map((result, index) => (
                <button
                  key={result.id}
                  onClick={() => handleSelect(result)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={cn(
                    'w-full flex items-center gap-4 px-4 py-3 text-left transition-colors',
                    index === selectedIndex
                      ? 'bg-highlight-hover'
                      : 'hover:bg-slate-50'
                  )}
                >
                  <div
                    className={cn(
                      'h-10 w-10 rounded-lg flex items-center justify-center shrink-0',
                      result.has_unread_changes
                        ? 'bg-red-100 text-red-600'
                        : 'bg-slate-100 text-slate-600'
                    )}
                  >
                    <Plane className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-slate-900">
                        {highlightMatch(result.pnr, query)}
                      </span>
                      {result.has_unread_changes && (
                        <span className="h-2 w-2 rounded-full bg-red-500" />
                      )}
                    </div>
                    <p className="text-sm text-slate-500 truncate">
                      {highlightMatch(result.lastname, query)}
                    </p>
                  </div>
                  <kbd className="hidden sm:block px-2 py-1 text-xs font-mono bg-slate-100 text-slate-500 rounded">
                    {index === selectedIndex ? 'Enter' : index + 1}
                  </kbd>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
